import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { PatchEmployeeBody, RevealEmployeeField } from "@evolua/api-client";
import { getApiClient } from "../auth/http.js";
import { downloadCsvText } from "../components/data/export-csv.js";
import { useSession } from "../session/SessionContext.js";
import { getNextCursor } from "./cursor.js";
import {
  exportDemoEmployees,
  getDemoEmployeeDetail,
  listDemoEmployees,
  patchDemoEmployee,
  revealDemoEmployeeField,
} from "./demo-employees.js";
import { listDemoCareerHistory } from "./demo-career-history.js";
import { queryKeys, type EmployeeListFilters } from "./query-keys.js";

export function useEmployeeDirectory(filters: EmployeeListFilters = {}) {
  const { isDemoMode } = useSession();

  return useInfiniteQuery({
    queryKey: queryKeys.employees.list(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      if (isDemoMode) {
        return listDemoEmployees({
          ...(pageParam ? { cursor: pageParam } : {}),
          limit: filters.limit ?? 25,
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.sort ? { sort: filters.sort } : {}),
          ...(filters.q ? { q: filters.q } : {}),
        });
      }
      return getApiClient().listEmployees({
        ...(pageParam ? { cursor: pageParam } : {}),
        ...(filters.limit !== undefined ? { limit: filters.limit } : {}),
        ...(filters.sort ? { sort: filters.sort } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.q ? { q: filters.q } : {}),
        ...(filters.companyId ? { companyId: filters.companyId } : {}),
        ...(filters.departmentId ? { departmentId: filters.departmentId } : {}),
        ...(filters.positionId ? { positionId: filters.positionId } : {}),
        ...(filters.managerId ? { managerId: filters.managerId } : {}),
      });
    },
    getNextPageParam: (last) => getNextCursor(last.pageInfo),
  });
}

export function useEmployee(id: string | undefined) {
  const { isDemoMode } = useSession();

  return useQuery({
    queryKey: queryKeys.employees.detail(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      if (!id) throw new Error("id obrigatório");
      if (isDemoMode) return getDemoEmployeeDetail(id);
      return getApiClient().getEmployee(id);
    },
  });
}

export function useEmployeeCareerHistory(id: string | undefined) {
  const { isDemoMode, session } = useSession();
  const canSeeSalary = Boolean(
    session?.permissions.includes("employee:salary_history") ||
    session?.permissions.includes("*"),
  );

  return useQuery({
    queryKey: queryKeys.employees.careerHistory(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      if (!id) throw new Error("id obrigatório");
      if (isDemoMode) return listDemoCareerHistory(id, canSeeSalary);
      return getApiClient().getEmployeeCareerHistory(id);
    },
  });
}

export function useRevealEmployeeField(employeeId: string | undefined) {
  const { isDemoMode } = useSession();

  return useMutation({
    mutationFn: async (field: RevealEmployeeField) => {
      if (!employeeId) throw new Error("id obrigatório");
      if (isDemoMode) return revealDemoEmployeeField(employeeId, field);
      return getApiClient().revealEmployeeField(employeeId, field);
    },
  });
}

export function usePatchEmployee(employeeId: string | undefined) {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: PatchEmployeeBody) => {
      if (!employeeId) throw new Error("id obrigatório");
      if (isDemoMode) return patchDemoEmployee(employeeId, body);
      return getApiClient().patchEmployee(employeeId, body);
    },
    onSuccess: (detail) => {
      if (!employeeId) return;
      queryClient.setQueryData(queryKeys.employees.detail(employeeId), detail);
    },
  });
}

/** Exportação auditada (API registra data.export; demo gera auditId local). */
export function useExportEmployees() {
  const { isDemoMode } = useSession();

  return useMutation({
    mutationFn: async (filters: EmployeeListFilters) => {
      if (isDemoMode) {
        const result = exportDemoEmployees({
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.sort ? { sort: filters.sort } : {}),
          ...(filters.q ? { q: filters.q } : {}),
        });
        downloadCsvText(result.filename, result.csv);
        return result;
      }
      const result = await getApiClient().exportEmployees({
        ...(filters.sort ? { sort: filters.sort } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.q ? { q: filters.q } : {}),
        ...(filters.companyId ? { companyId: filters.companyId } : {}),
        ...(filters.departmentId ? { departmentId: filters.departmentId } : {}),
        ...(filters.positionId ? { positionId: filters.positionId } : {}),
        ...(filters.managerId ? { managerId: filters.managerId } : {}),
      });
      downloadCsvText(result.filename, result.csv);
      return result;
    },
  });
}
